// Blueprint's Setup pages' rules on the SettingsPage pattern (BL-189): each section of RULE_SECTIONS a
// SettingsSection, each rule a SettingRow with its control drawn from catalogued fields. As RuleSection.tsx
// draws them in Blueprint, with nothing changed: a change saves itself (a choice at once, typing when
// it's left or on Enter, Escape puts the saved value back), a change with consequences asks first, a
// rule that depends on another sits under it while that one is on, a department's own value folds under
// its rule, and someone who can't change a rule reads its value as words. Riverstone keeps RuleSection.tsx.
import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent, type ReactNode } from 'react';
import { RpcError } from '@flowology/client';
import { Button, Checkbox, Link, Segmented, SelectField, TextField } from '@flowology/ui-web/fl';
import {
  SettingAsk,
  SettingFieldLine,
  SettingFields,
  SettingRow,
  SettingScope,
  SettingScopes,
  SettingsSection,
  type SettingSource,
  type SettingState,
} from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import {
  centsToDollars,
  dollarsToCents,
  hoursAsMinutes,
  minutesAsHours,
  OVERTIME_PARTS,
  ruleEditable,
  rulesCardLine,
  sectionsOf,
  settingWords,
  SIGN_IN_METHODS,
  toggleMethod,
  TWO_FACTOR_MODES,
  TWO_FACTOR_ROLES,
  WEEKDAYS,
  wordsOf,
  type AnswerHours,
  type Control,
  type Overtime,
  type Rights,
  type Rule,
  type RulePage,
  type SettingRow as Row,
  type TwoFactor,
} from './settings-model.ts';
import { useFoundSetting, useRuleData, type RuleData, type StaffName } from './rule-data.ts';
import { financingProblem, type FinancingSetting } from './financing-model.ts';
import type { FinancingPlan } from '@flowology/domain';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

// the controls whose parts are too wide to sit beside the words: under them instead
const WIDE: readonly Control['kind'][] = ['overtime', 'twoFactor', 'answerHours', 'paragraph'];
const DAYS = WEEKDAYS.map(([d, l]) => ({ id: String(d), label: l }));
const toggled = (ids: string[]) => ids.map(Number).sort();

/**
 * A Setup page's rule sections (RULE_SECTIONS), before its own lists (`top`) or after them. A section
 * with no rules draws nothing; `inside` puts a page's own part in a section, under its rules, or a part
 * made from the section's settings as they are now.
 */
export function SetupRuleSections({
  page,
  top = false,
  rights,
  inside = {},
}: {
  page: RulePage;
  top?: boolean;
  rights: Rights;
  inside?: Record<string, ReactNode | ((rows: readonly Row[]) => ReactNode)>;
}) {
  const sections = sectionsOf(page, top).filter(
    (s) => s.rules.length > 0 || !!s.fixed?.length || inside[s.id] !== undefined,
  );
  const data = useRuleData(sections.length > 0);
  return (
    <>
      {sections.map((s) => {
        const part = inside[s.id];
        const status = data.rows ? 'ready' : data.error ? 'failed' : 'loading';
        const note = data.rows ? rulesCardLine(s.rules, rights, data.rows) : null;
        return (
          <SettingsSection
            key={s.id}
            title={s.title}
            line={s.line}
            {...(note ? { note } : {})}
            status={status}
            failedText={data.error ?? undefined}
            onRetry={() => void data.load()}
          >
            <SetupRuleRows rules={s.rules} data={data} rights={rights} />
            {s.fixed?.map((f) => (
              <SettingRow key={f.label} name={f.label} help={f.help} control="Always" />
            ))}
            {typeof part === 'function' ? part(data.rows ?? []) : part}
          </SettingsSection>
        );
      })}
    </>
  );
}

/** A section's rules as SettingRows: those the server has, a dependent one only while its rule is on. */
export function SetupRuleRows({
  rules,
  data,
  rights,
}: {
  rules: readonly Rule[];
  data: RuleData;
  rights: Rights;
}) {
  const byKey = new Map((data.rows ?? []).map((r) => [r.key, r]));
  const parentOf = (rule: Rule) =>
    rule.under && rules.some((r) => r.key === rule.under) ? rule.under : null;
  return (
    <>
      {rules.map((rule) => {
        const row = byKey.get(rule.key);
        const parent = parentOf(rule);
        if (!row || (parent && byKey.get(parent)?.value !== true)) return null;
        return (
          <SetupRuleRow
            key={rule.key}
            rule={rule}
            row={row}
            data={data}
            canEdit={ruleEditable(rule, rights, row)}
            isSub={!!parent}
            warning={rule.warn?.(row.value, (k) => byKey.get(k)?.value) ?? null}
          />
        );
      })}
    </>
  );
}

function SetupRuleRow({
  rule,
  row,
  data,
  canEdit,
  isSub,
  warning,
}: {
  rule: Rule;
  row: Row;
  data: RuleData;
  canEdit: boolean;
  isSub: boolean;
  warning: string | null;
}) {
  const id = `rule-${rule.key}`;
  const found = useFoundSetting(rule.key, () => document.getElementById(id));
  const editor = useEditor({
    id: `set-${rule.key}`,
    label: rule.label,
    control: rule.control,
    value: row.value,
    staff: data.staff,
    disabled: !canEdit,
    confirm: rule.confirm,
    onSave: async (value) => {
      await client.call('settings.set', { key: rule.key, value });
      await data.load();
    },
  });
  const source = useSource(rule, row, canEdit, data.load);
  // a department's own value, named only where there is one: one by name, more as a count
  const differ = row.overrides.filter((o) => o.scopeType === 'business_unit');
  const one = differ.length === 1 ? data.units.find((u) => u.id === differ[0]!.scopeId) : undefined;
  const summary = one
    ? `${one.name}: ${settingWords(rule.control, differ[0]!.value, data.staff)}`
    : differ.length > 1
      ? `${differ.length} departments differ`
      : 'Set for one department';
  const perUnit =
    row.scopes.includes('business_unit') && data.units.length > 0 && (canEdit || differ.length > 0);
  return (
    <SettingRow
      id={id}
      name={rule.label}
      {...(rule.ownerOnly ? { tag: 'Owner only' } : {})}
      help={rule.help}
      {...(warning ? { warning } : {})}
      control={editor.control}
      {...(rule.control.kind === 'number' && canEdit ? { unit: rule.control.unit } : {})}
      {...(editor.state ? { state: editor.state } : {})}
      ask={editor.ask}
      source={source}
      isWide={WIDE.includes(rule.control.kind)}
      isSub={isSub}
      isFound={found}
      onControlBlur={editor.onBlur}
      onControlKeyDown={editor.onKeyDown}
      {...(perUnit
        ? {
            more: (
              <SettingScopes summary={summary}>
                {data.units.map((u) => (
                  <UnitScope key={u.id} rule={rule} row={row} unit={u} data={data} canEdit={canEdit} />
                ))}
              </SettingScopes>
            ),
          }
        : {})}
    />
  );
}

/** One department's own value of a rule, or the company's, with the way back to the company's. */
function UnitScope({
  rule,
  row,
  unit,
  data,
  canEdit,
}: {
  rule: Rule;
  row: Row;
  unit: { id: string; name: string };
  data: RuleData;
  canEdit: boolean;
}) {
  const o = row.overrides.find((x) => x.scopeType === 'business_unit' && x.scopeId === unit.id);
  const at = { key: rule.key, scopeType: 'business_unit', scopeId: unit.id };
  const editor = useEditor({
    id: `set-${rule.key}-${unit.id}`,
    label: `${rule.label} for ${unit.name}`,
    control: rule.control,
    value: o ? o.value : row.value,
    staff: data.staff,
    disabled: !canEdit,
    confirm: rule.confirm,
    onSave: async (value) => {
      await client.call('settings.set', { ...at, value });
      await data.load();
    },
    ...(o && canEdit
      ? {
          onClear: async () => {
            await client.call('settings.clear', at);
            await data.load();
          },
        }
      : {}),
  });
  return (
    <SettingScope
      name={unit.name}
      {...(!o ? { note: 'uses the company value' } : {})}
      control={
        <>
          {editor.control}
          {editor.ask}
          {editor.clear}
          {editor.state?.status === 'saved' ? 'Saved' : null}
          {editor.state?.status === 'failed' ? editor.state.text : null}
        </>
      }
      onControlBlur={editor.onBlur}
      onControlKeyDown={editor.onKeyDown}
    />
  );
}

/**
 * Whether a value is still Flowology's default, or the company's own with the way back to the default
 * (settings.clear). A rule with consequences asks before it goes back, as it does to change.
 */
function useSource(rule: Rule, row: Row, canEdit: boolean, onSaved: () => Promise<void>): SettingSource {
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const back = async () => {
    setAsking(false);
    try {
      await client.call('settings.clear', { key: rule.key });
      setError(null);
      await onSaved();
    } catch (e) {
      setError(message(e));
    }
  };
  if (row.source === 'invalid') return { from: 'unreadable' };
  if (row.source === 'default') return { from: 'default' };
  return {
    from: 'changed',
    ...(canEdit ? { onBack: () => (rule.confirm ? setAsking(true) : void back()) } : {}),
    ...(asking
      ? {
          asking: (
            <SettingAsk
              words="Put back Flowology’s default?"
              onApply={() => void back()}
              onCancel={() => setAsking(false)}
            />
          ),
        }
      : {}),
    ...(error ? { error } : {}),
  };
}

interface EditorProps {
  id: string;
  label: string;
  control: Control;
  value: unknown;
  staff: StaffName[];
  disabled: boolean;
  confirm?: Rule['confirm'];
  onSave: (value: unknown) => Promise<void>;
  onClear?: () => Promise<void>;
}

/**
 * One value's control, saving itself: a choice at once, typing when it's left or on Enter; a change with
 * consequences says what it does first. Someone who can't change it reads its value as words.
 */
function useEditor({ id, label, control, value, staff, disabled, confirm, onSave, onClear }: EditorProps) {
  const [draft, setDraft] = useState<unknown>(value);
  // what a typed control holds as text: dollars, a list of words, the overtime hours
  const asText = () =>
    control.kind === 'money'
      ? value === null || value === undefined
        ? ''
        : centsToDollars(Number(value))
      : control.kind === 'words'
        ? (Array.isArray(value) ? (value as string[]) : []).join(control.perLine ? '\n' : ', ')
        : '';
  const asHours = () =>
    control.kind === 'overtime'
      ? Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, minutesAsHours((value as Overtime)[k])]))
      : {};
  const [text, setText] = useState(asText);
  const [hours, setHours] = useState<Record<string, string>>(asHours);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [asking, setAsking] = useState<{ value: unknown; words: string } | null>(null);
  // the value last sent, so leaving the field after a choice saved it doesn't send it again
  const sent = useRef<string | null>(null);
  useEffect(() => {
    setDraft(value);
    setText(asText());
    setHours(asHours());
    sent.current = null;
    // again only when the saved value or the control changes
  }, [value, control.kind]);
  const overtime = (): Overtime | null => {
    const o = Object.fromEntries(OVERTIME_PARTS.map(([k]) => [k, hoursAsMinutes(hours[k] ?? '')]));
    return Object.values(o).some((m) => m === null) ? null : (o as unknown as Overtime);
  };
  // a money field that may be left empty (the ad cap) saves empty as none
  const empty = control.kind === 'money' && !!control.none && text.trim() === '';
  const out =
    control.kind === 'money'
      ? empty
        ? null
        : dollarsToCents(text)
      : control.kind === 'words'
        ? wordsOf(text, control.perLine)
        : control.kind === 'overtime'
          ? overtime()
          : draft;
  const dirty = JSON.stringify(out) !== JSON.stringify(value);
  // back to the saved value: a refusal of another value no longer applies (QA-560)
  useEffect(() => {
    if (!dirty) setError(null);
  }, [dirty]);
  const save = async (v: unknown = out) => {
    setSaved(false); // a refusal never sits beside an earlier "Saved" (QA-560)
    if (control.kind === 'money' && v === null && !empty) return setError('Enter an amount like 89.00.');
    if (control.kind === 'overtime' && v === null) return setError('Enter each as hours, like 8 or 7.5.');
    if (control.kind === 'paragraph') {
      const missing = control.keep?.find((k) => !String(v).includes(k));
      if (missing) return setError(`Keep ${missing} in it: it is filled in for each customer.`);
    }
    setBusy(true);
    try {
      sent.current = JSON.stringify(v);
      await onSave(v);
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      sent.current = null;
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };
  // send a changed value now, or first say what it does
  const commit = (v: unknown) => {
    const j = JSON.stringify(v);
    if (busy || j === JSON.stringify(value) || j === sent.current) return;
    const words = confirm?.(v, value) ?? null;
    if (words) return setAsking({ value: v, words });
    void save(v);
  };
  // a choice: shown at once, and saved at once
  const pick = (v: unknown) => {
    setDraft(v);
    commit(v);
  };
  const putBack = () => {
    setAsking(null);
    setDraft(value);
    setText(asText());
    setHours(asHours());
  };
  const state: SettingState | null = error
    ? { status: 'failed', text: error }
    : saved
      ? { status: 'saved' }
      : null;
  const events = {
    onBlur: (e: FocusEvent<HTMLElement>) => {
      if (!disabled && !e.currentTarget.contains(e.relatedTarget as Node | null)) commit(out);
    },
    onKeyDown: (e: KeyboardEvent<HTMLElement>) => {
      if (disabled) return;
      if (e.key === 'Escape') putBack();
      else if (e.key === 'Enter' && e.target instanceof HTMLInputElement) commit(out);
    },
  };
  const ask = asking ? (
    <SettingAsk
      words={asking.words}
      isBusy={busy}
      onApply={() => void save(asking.value).then(() => setAsking(null))}
      onCancel={putBack}
    />
  ) : null;
  const clear =
    onClear && !dirty ? (
      <Button
        variant="secondary"
        size="sm"
        isBusy={busy}
        onPress={() => void onClear().catch((e: unknown) => setError(message(e)))}
      >
        Back to the company’s value
      </Button>
    ) : null;
  // someone who reads the rules but can't change this one sees its value as words, not a dead field
  const shown: ReactNode = disabled
    ? settingWords(control, value, staff)
    : controlFor({ id, label, control, draft, text, hours, staff, setDraft, setText, setHours, pick });
  return { control: shown, state, ask, clear, ...events };
}

interface ControlArgs {
  id: string;
  label: string;
  control: Control;
  draft: unknown;
  text: string;
  hours: Record<string, string>;
  staff: StaffName[];
  setDraft: (v: unknown) => void;
  setText: (v: string) => void;
  setHours: (v: Record<string, string>) => void;
  pick: (v: unknown) => void;
}

/** The catalogued fields a control is drawn with. */
function controlFor(a: ControlArgs): ReactNode {
  const { label, control, draft, text, hours, staff, setDraft, setText, setHours, pick } = a;
  const time = (name: string, v: string, set: (t: string) => void) => (
    <TextField label={name} hideLabel type="time" width="time" value={v} onChange={set} />
  );
  switch (control.kind) {
    case 'bool':
      return (
        <Checkbox isSelected={draft === true} onChange={pick}>
          {draft === true ? 'On' : 'Off'}
        </Checkbox>
      );
    case 'select':
      return (
        <SelectField
          label={label}
          hideLabel
          value={String(draft)}
          options={control.options.map(([v, l]) => ({ value: String(v), label: l }))}
          onChange={(v) => pick(control.options.find(([o]) => String(o) === v)?.[0] ?? v)}
        />
      );
    case 'number':
      return (
        <TextField
          label={label}
          hideLabel
          type="number"
          width="number"
          {...(control.min !== undefined ? { min: control.min } : {})}
          {...(control.max !== undefined ? { max: control.max } : {})}
          value={String(draft ?? '')}
          onChange={(v) => setDraft(v === '' ? null : Number(v))}
        />
      );
    case 'money':
      return (
        <>
          $
          <TextField
            label={label}
            hideLabel
            width="number"
            inputMode="decimal"
            {...(control.none ? { placeholder: control.none } : {})}
            value={text}
            onChange={setText}
          />
        </>
      );
    case 'text':
    case 'colour':
      return (
        <TextField
          label={label}
          hideLabel
          value={typeof draft === 'string' ? draft : ''}
          placeholder={control.kind === 'colour' ? '#RRGGBB, or blank for Flowology’s own' : 'Not set'}
          onChange={(v) => setDraft(v.trim() === '' ? null : control.kind === 'colour' ? v.trim() : v)}
        />
      );
    case 'quietHours': {
      const q = draft as { start: string; end: string } | null;
      return (
        <>
          <Checkbox isSelected={!!q} onChange={(on) => pick(on ? { start: '21:00', end: '08:00' } : null)}>
            {q ? 'From' : 'None'}
          </Checkbox>
          {q && (
            <>
              {time(`${label}: from`, q.start, (start) => setDraft({ ...q, start }))}
              to
              {time(`${label}: to`, q.end, (end) => setDraft({ ...q, end }))}
            </>
          )}
        </>
      );
    }
    case 'workingHours': {
      const w = draft as { open: string; close: string; days: number[] };
      return (
        <>
          {time(`${label}: opens at`, w.open, (open) => setDraft({ ...w, open }))}
          to
          {time(`${label}: closes at`, w.close, (close) => setDraft({ ...w, close }))}
          <Segmented
            label={`${label}: days`}
            selectionMode="multiple"
            items={DAYS}
            selectedKeys={w.days.map(String)}
            onSelectionChange={(ids) => pick({ ...w, days: toggled(ids) })}
          />
        </>
      );
    }
    case 'methods': {
      const on = draft as string[];
      return (
        <Segmented
          label={label}
          selectionMode="multiple"
          items={SIGN_IN_METHODS.map(([m, l]) => ({
            id: m,
            label: l,
            isDisabled: on.length === 1 && on.includes(m),
          }))}
          selectedKeys={on}
          onSelectionChange={(ids) => {
            const m = [...on, ...ids].find((x) => on.includes(x) !== ids.includes(x));
            if (m) pick(toggleMethod(on, m as (typeof on)[number]));
          }}
        />
      );
    }
    case 'financing':
      // BL-005: the lender, its link and the plans, saved together when the focus leaves them
      return <FinancingFields value={draft as FinancingSetting} onChange={setDraft} />;
    case 'paragraph':
      return (
        <TextField
          label={label}
          hideLabel
          multiline
          value={typeof draft === 'string' ? draft : ''}
          onChange={setDraft}
        />
      );
    case 'words':
      return (
        <TextField
          label={label}
          hideLabel
          {...(control.perLine ? { multiline: true } : {})}
          value={text}
          placeholder="None"
          onChange={setText}
        />
      );
    case 'time':
      return time(label, typeof draft === 'string' ? draft : '', setDraft);
    case 'overtime':
      return (
        <>
          {OVERTIME_PARTS.map(([k, words]) => (
            <TextField
              key={k}
              label={`${label}: ${words}`}
              hideLabel
              width="number"
              inputMode="decimal"
              unit={words}
              value={hours[k] ?? ''}
              onChange={(v) => setHours({ ...hours, [k]: v })}
            />
          ))}
        </>
      );
    case 'twoFactor': {
      const t = draft as TwoFactor;
      const roles = [
        ...TWO_FACTOR_ROLES,
        // a company's own role already in the list keeps its place
        ...t.roles.filter((r) => !TWO_FACTOR_ROLES.some(([k]) => k === r)).map((r) => [r, r] as const),
      ];
      return (
        <>
          <SelectField
            label={label}
            hideLabel
            value={t.mode}
            options={TWO_FACTOR_MODES.map(([m, l]) => ({ value: m, label: l }))}
            onChange={(mode) => pick({ ...t, mode: mode as TwoFactor['mode'] })}
          />
          {t.mode === 'roles' && (
            <Segmented
              label={`${label}: the roles`}
              selectionMode="multiple"
              items={roles.map(([r, l]) => ({ id: r, label: l }))}
              selectedKeys={t.roles}
              onSelectionChange={(ids) =>
                pick({ ...t, roles: roles.map(([r]) => r).filter((r) => ids.includes(r)) })
              }
            />
          )}
          {t.mode !== 'off' && (
            <Checkbox isSelected={t.appOnly} onChange={(appOnly) => pick({ ...t, appOnly })}>
              Authenticator app only
            </Checkbox>
          )}
        </>
      );
    }
    case 'answerHours': {
      // the office's hours it answers outside, or always
      const h = draft as AnswerHours;
      return (
        <>
          <SelectField
            label={label}
            hideLabel
            value={h.mode}
            options={[
              { value: 'after_hours', label: 'Outside office hours' },
              { value: 'always', label: 'Always' },
            ]}
            onChange={(mode) => pick({ ...h, mode: mode as AnswerHours['mode'] })}
          />
          {h.mode === 'after_hours' && (
            <>
              {time(`${label}: the office opens`, h.open, (open) => setDraft({ ...h, open }))}
              to
              {time(`${label}: the office closes`, h.close, (close) => setDraft({ ...h, close }))}
              <Segmented
                label={`${label}: the office's days`}
                selectionMode="multiple"
                items={DAYS}
                selectedKeys={h.days.map(String)}
                onSelectionChange={(ids) => pick({ ...h, days: toggled(ids) })}
              />
            </>
          )}
        </>
      );
    }
    case 'alertTo':
      return (
        <SelectField
          label={label}
          hideLabel
          value={String(draft)}
          options={[
            { value: 'managers', label: 'The owner and managers' },
            ...staff.map((s) => ({ value: s.id, label: s.displayName })),
          ]}
          onChange={pick}
        />
      );
  }
}

/**
 * Consumer financing's fields (BL-005), as FinancingPlans draws them in Riverstone: the lender, the link a
 * customer applies on, then a line for each plan (months, annual rate, smallest amount financed), added
 * or removed; one setting, saved as a whole.
 */
function FinancingFields({
  value,
  onChange,
}: {
  value: FinancingSetting;
  onChange: (v: FinancingSetting) => void;
}) {
  const set = (p: Partial<FinancingSetting>) => onChange({ ...value, ...p });
  const plan = (i: number, p: FinancingPlan) => set({ plans: value.plans.map((x, j) => (j === i ? p : x)) });
  return (
    <SettingFields label="Consumer financing" error={financingProblem(value)}>
      <TextField label="Lender" value={value.lender} onChange={(lender) => set({ lender })} />
      <TextField
        label="Link a customer applies on (optional)"
        value={value.applyUrl ?? ''}
        placeholder="https://"
        onChange={(v) => set({ applyUrl: v.trim() || null })}
      />
      {value.plans.map((p, i) => (
        <SettingFieldLine key={i} label={`Plan ${i + 1}`}>
          <TextField
            label="Months"
            inputMode="numeric"
            value={String(p.months)}
            onChange={(v) => plan(i, { ...p, months: Number(v) })}
          />
          <TextField
            label="Annual rate %"
            inputMode="decimal"
            value={String(p.aprPercent)}
            onChange={(v) => plan(i, { ...p, aprPercent: Number(v) })}
          />
          <TextField
            label="From $ (optional)"
            inputMode="decimal"
            value={p.minCents ? String(p.minCents / 100) : ''}
            onChange={(v) => {
              const d = Number(v);
              const { minCents: _, ...rest } = p;
              plan(i, d > 0 ? { ...rest, minCents: Math.round(d * 100) } : rest);
            }}
          />
          <Link onPress={() => set({ plans: value.plans.filter((_, j) => j !== i) })}>Remove plan</Link>
        </SettingFieldLine>
      ))}
      <Link
        isDisabled={value.plans.length >= 12}
        onPress={() => set({ plans: [...value.plans, { months: 60, aprPercent: 9.99 }] })}
      >
        Add a plan
      </Link>
    </SettingFields>
  );
}
